import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { SideNavDashboard, SideNavMatter } from '@/components/side-nav/SideNav'
import { SideNavRail } from '@/components/side-nav/SideNavRail'
import { PAXTON_LOGO_MARK_URL, SIDENAV_CONTRACTED_WIDTH, SIDENAV_WIDTH } from '@/components/side-nav/constants'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAppDispatch } from '@/store/store-hooks'
import { toggleSideNavCollapsed } from '@/store/slices/ui-state.slice'
import { useSideNavConfig } from './hooks/useSideNavConfig'
import SidebarAccountMenu from './SidebarAccountMenu'
import SidebarItemSupport from './SidebarItemSupport'
import SidebarItemAnonSignIn from './SidebarItemAnonSignIn'
import SidebarSyncFilesCard from './SidebarSyncFilesCard'

type DesktopSidebarProps = {
  /** Whether the nav is collapsed, from `uiState.sideNavCollapsed`. */
  collapsed: boolean
}

/**
 * Desktop Sidebar
 *
 * Desktop version of the main navigation component to display feature navigation items.
 * - Static sidebar rendered on desktop (≥ lg) only
 * - `collapsed` contracts the nav to an icon rail
 */
export default function DesktopSidebar(props: DesktopSidebarProps) {
  const { collapsed } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  const config = useSideNavConfig()

  const contractToggleRef = useRef<HTMLButtonElement>(null)
  const expandToggleRef = useRef<HTMLButtonElement>(null)
  const moveFocusOnToggle = useRef(false)

  const handleToggle = () => {
    moveFocusOnToggle.current = true
    dispatch(toggleSideNavCollapsed())
    trackEvent(AnalyticsEvent.SideNavToggled, { sideNavCollapsed: !collapsed })
  }

  useEffect(() => {
    if (!moveFocusOnToggle.current) return
    moveFocusOnToggle.current = false
    const replacement = collapsed ? expandToggleRef.current : contractToggleRef.current
    replacement?.focus()
  }, [collapsed])

  const renderFooter = () => (
    <>
      <SidebarItemSupport />
      <SidebarItemAnonSignIn />
      <SidebarSyncFilesCard />
      <SidebarAccountMenu />
    </>
  )

  const renderRailFooter = () => (
    <>
      <SidebarItemAnonSignIn contracted />
      <SidebarAccountMenu contracted />
    </>
  )

  const expandedNav =
    config.variant === 'dashboard' ? (
      <SideNavDashboard
        logoUrl={kDefaultLogoPath}
        hasFixedLogoHeight={true}
        activeItemId={config.activeItemId}
        items={config.items}
        renderFooter={renderFooter}
        onContract={handleToggle}
        contractToggleRef={contractToggleRef}
      />
    ) : (
      <SideNavMatter
        logoUrl={kDefaultLogoPath}
        hasFixedLogoHeight={true}
        activeItemId={config.activeItemId}
        topItems={config.topItems}
        matterRecords={config.matterRecords}
        chatHistory={config.chatHistory}
        renderFooter={renderFooter}
        onContract={handleToggle}
        contractToggleRef={contractToggleRef}
      />
    )

  return (
    <div className="flex h-full">
      <div
        className="relative inset-y-0 z-50 h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-out motion-reduce:transition-none"
        style={{ width: collapsed ? SIDENAV_CONTRACTED_WIDTH : SIDENAV_WIDTH }}
      >
        <div
          className={clsx(
            'absolute inset-y-0 left-0 transition-opacity duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'invisible opacity-0' : 'opacity-100'
          )}
          style={{ width: SIDENAV_WIDTH }}
        >
          {expandedNav}
        </div>

        <div
          className={clsx(
            'absolute inset-y-0 left-0 transition-opacity duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'opacity-100' : 'invisible opacity-0'
          )}
          style={{ width: SIDENAV_CONTRACTED_WIDTH }}
        >
          <SideNavRail
            logoUrl={PAXTON_LOGO_MARK_URL}
            activeItemId={config.activeItemId}
            items={config.variant === 'dashboard' ? config.items : config.topItems}
            matterRecords={config.variant === 'matter' ? config.matterRecords : undefined}
            showNewChat={config.variant === 'matter' && Boolean(config.chatHistory) && config.chatHistory?.allowNewChat !== false}
            activeChatId={config.variant === 'matter' ? config.chatHistory?.activeChatId : undefined}
            onExpand={handleToggle}
            expandToggleRef={expandToggleRef}
            renderFooter={renderRailFooter}
          />
        </div>
      </div>
    </div>
  )
}
